import isNull from 'lodash/isNull'
import { computed, makeObservable } from 'mobx'
import moment from 'moment'

import { ModelWithPolicy } from 'modules/core/lib/modelWithPolicy'
import LeavePolicy from 'modules/core/store/models/policies/LeavePolicy'

import documents from 'modules/documents/store/collections/documents'
import type Document from 'modules/documents/store/models/Document'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

const DEFAULT_COLOR = '#07A2AD' // viridian/1000

export const HALF_DAY_TYPES = {
  beggining_of_day: 'beggining_of_day',
  end_of_day: 'end_of_day',
}

interface ILeave {
  [key: string]: any
}

class Leave extends ModelWithPolicy<ILeave> {
  static buildNullLeave = (id: number) => ({
    id,
    employee_id: null,
    leave_type_id: null,
    start_on: null,
    approved: null,
    hours_amount: null,
    accrues: false,
    medical_leave_type: null,
    permissions: {},
    leave_confirmation_fields: {},
    french_calendar_flag_enabled: false,
    delegatedApprover: null,
  })

  static medicalLeaveTypes = {
    accident_working: 'accident_working',
    sickness_common: 'sickness_common',
    accident_other: 'accident_other',
    sickness_professional: 'sickness_professional',
    paternity: 'paternity',
    maternity: 'maternity',
    lactation: 'lactation',
  }

  constructor(attr: ILeave = {}) {
    super(attr)
    makeObservable(this, {
      leaveType: computed,
      typeColor: computed,
      documents: computed,
      hoursAmountInCents: computed,
      isSick: computed,
      delegatedApprover: computed,
    })
  }

  static urlRoot() {
    return '/leaves'
  }

  get policy() {
    return new LeavePolicy(this)
  }

  get leaveType(): LeaveType | null | undefined {
    const leaveTypeId = this.attributes.leave_type_id

    return leaveTypes.get(leaveTypeId)
  }

  get delegatedApprover(): string | undefined {
    if (!this.attributes.delegated_approver) return undefined
    return this.attributes.delegated_approver.access_id
  }

  get typeColor(): string {
    return this.leaveType ? `#${this.leaveType.attributes.color}` : DEFAULT_COLOR
  }

  get documents(): Document[] {
    return documents.filter({
      leave_id: this.attributes.id,
    })
  }

  get hoursAmountInCents(): number | null | undefined {
    const hoursAmount = this.attributes.hours_amount

    return hoursAmount ? hoursAmount * 100 : null
  }

  isPending(): boolean {
    return isNull(this.attributes.approved)
  }

  isApproved(): boolean {
    return this.attributes.approved
  }

  isRejected(): boolean {
    return !this.isPending() && !this.isApproved()
  }

  isHalfDayBeginningOfDay(): boolean {
    return this.attributes.half_day === HALF_DAY_TYPES.beggining_of_day
  }

  frenchCalendarFlagEnabled(): boolean {
    return this.attributes.french_calendar_flag_enabled
  }

  sickLeaveEnd(start: moment.Moment) {
    const today = moment()
    const diff = start.diff(today, 'days')

    if (diff < 0) return today

    return start
  }

  get startOn(): moment.Moment {
    return moment(this.attributes.start_on)
  }

  get isSick(): boolean {
    return this.attributes.type === 'sick'
  }
}

export default Leave
